import {getAppConfig} from "@vesta/app-config/sol";
import {Envelope, Telephone} from "react-bootstrap-icons";
import type {ActionFunctionArgs} from "react-router";
import {Form, useActionData, useNavigation} from "react-router";

export function meta() {
    return [{title: "Contact, Intellsys OS"}, {name: "description", content: "Talk to us about running your venture on Intellsys OS."}];
}

export async function action({request}: ActionFunctionArgs) {
    const formData = await request.formData();
    const submission = {
        name: String(formData.get("name") ?? "").trim(),
        email: String(formData.get("email") ?? "").trim(),
        countryCode: String(formData.get("countryCode") ?? "").trim(),
        phone: String(formData.get("phone") ?? "").trim(),
        message: String(formData.get("message") ?? "").trim(),
    };

    // Name and message are always required; email OR phone is required; the
    // ones that ARE provided must look roughly valid.
    if (submission.name.length === 0) {
        return {success: false, error: "Please enter your name."};
    }
    if (submission.message.length === 0) {
        return {success: false, error: "Please write a message."};
    }
    if (submission.email.length === 0 && submission.phone.length === 0) {
        return {success: false, error: "Please share an email or phone number so we can reach you."};
    }
    if (submission.email.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(submission.email)) {
        return {success: false, error: "That email doesn't look right, please check the format."};
    }
    if (submission.phone.length > 0) {
        const digits = submission.phone.replace(/\D/g, "");
        if (digits.length < 6) {
            return {success: false, error: "That phone number looks too short, please check it."};
        }
    }

    // POST to Pluto's lead-capture endpoint. Base URL comes from sol's
    // AppConfig (`PLUTO_BASE_URL` env var), so the same code points at the
    // dev / staging / prod backend depending on environment.
    const {plutoBaseUrl} = getAppConfig();
    try {
        const response = await fetch(`${plutoBaseUrl}/contact-us-leads/create`, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({
                name: submission.name,
                email: submission.email,
                countryCode: submission.countryCode,
                phone: submission.phone,
                message: submission.message,
            }),
        });
        if (!response.ok) {
            const body = await response.text();
            console.error("[contact] backend returned", response.status, body);
            return {success: false, error: "Something went wrong on our end. Please try again, or email us directly."};
        }
    } catch (err) {
        console.error("[contact] failed to reach backend", err);
        return {success: false, error: "We couldn't reach our servers. Please try again, or email us directly."};
    }

    return {success: true};
}

/* Same checkmark SVG used by Hero, keeps bullet styling consistent across pages. */
function Checkmark() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            className="w-4 h-4 shrink-0 mt-[3px] text-primary-500"
            stroke="currentColor"
            strokeWidth="3"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 12l5 5L20 7"
            />
        </svg>
    );
}

const bullets = [
    {lead: "Response in one business day.", body: "Real human, not a drip sequence."},
    {lead: "Tailored walkthrough.", body: "Demo specific to your venture, not a canned deck."},
    {lead: "Direct line to the team.", body: "Talk to the people who build it."},
];

const inputClasses =
    "text-medium text-text-primary bg-[#FFFFFF] border border-surface-lines-and-borders rounded-sm lg:rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 transition-colors";

export default function RouteComponent() {
    const actionData = useActionData<typeof action>();
    const nav = useNavigation();
    const isSubmitting = nav.state === "submitting";
    const submitted = actionData?.success === true;
    const errorMessage = actionData?.success === false ? actionData.error : null;

    return (
        <section className="gj-px-screen-edge-constrained pt-[calc(var(--gj-header-height)+3rem)] pb-lg2 min-h-screen">
            <div className="grid grid-cols-1 lg:grid-cols-[5fr_6fr] gap-lg lg:gap-lg2 items-start">
                {/* Left column, pitch + direct contact */}
                <div className="grid gap-md max-w-[32rem]">
                    <h1 className="text-heading2-25 text-text-primary tracking-tight">
                        Talk to <span className="text-gradient-blue">us</span>.
                    </h1>
                    <p className="text-medium text-text-secondary">Tell us about your venture, we&apos;ll respond within one business day.</p>

                    <ul className="grid gap-3 mt-sm2">
                        {bullets.map((b) => (
                            <li
                                key={b.lead}
                                className="grid grid-cols-[auto_1fr] gap-2 items-start"
                            >
                                <Checkmark />
                                <p className="text-medium text-text-body">
                                    <strong className="text-medium-b text-text-primary font-semibold">{b.lead}</strong> {b.body}
                                </p>
                            </li>
                        ))}
                    </ul>

                    <div className="grid gap-2 mt-md pt-md border-t border-surface-lines-and-borders">
                        <p className="text-badge-text text-text-tertiary">Or reach us directly</p>
                        <a
                            href="mailto:scale@growthjockey.com"
                            className="text-medium text-text-primary hover:text-primary-500 transition-colors no-underline inline-flex items-center gap-2 w-fit"
                        >
                            <Envelope
                                size={14}
                                className="shrink-0"
                            />
                            scale@growthjockey.com
                        </a>
                        <a
                            href="tel:+917018000000"
                            className="text-medium text-text-primary hover:text-primary-500 transition-colors no-underline inline-flex items-center gap-2 w-fit"
                        >
                            <Telephone
                                size={14}
                                className="shrink-0"
                            />
                            +91 70180 00000
                        </a>
                    </div>
                </div>

                {/* Right column, form card */}
                <div className="bg-[#FFFFFF] border border-surface-lines-and-borders rounded-sm lg:rounded-lg p-md3 md:p-lg shadow-[0_8px_24px_-12px_rgba(0,0,0,0.08)]">
                    {submitted ? (
                        <div className="grid place-items-center gap-2 py-md3 text-center">
                            <div className="cta-bg-gradient text-[#FFFFFF] rounded-full w-10 h-10 flex items-center justify-center text-xl">✓</div>
                            <p className="text-main-title text-text-primary mt-sm">Thanks, we&apos;ll be in touch.</p>
                            <p className="text-medium text-text-secondary">A real human reads every submission.</p>
                        </div>
                    ) : (
                        <Form
                            method="post"
                            className="grid gap-md"
                        >
                            <div className="grid gap-2">
                                <label
                                    htmlFor="name"
                                    className="text-medium-b text-text-primary"
                                >
                                    Name
                                </label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    autoComplete="name"
                                    required
                                    className={inputClasses}
                                />
                            </div>

                            <p className="text-nav-text text-text-tertiary">Share your email or phone, at least one is needed.</p>

                            <div className="grid gap-2">
                                <label
                                    htmlFor="email"
                                    className="text-medium-b text-text-primary"
                                >
                                    Email
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    placeholder="you@company.com"
                                    className={inputClasses}
                                />
                            </div>

                            <div className="grid gap-2">
                                <label
                                    htmlFor="phone"
                                    className="text-medium-b text-text-primary"
                                >
                                    Phone
                                </label>
                                <div className="grid grid-cols-[6rem_1fr] gap-2">
                                    <input
                                        id="countryCode"
                                        name="countryCode"
                                        type="text"
                                        inputMode="tel"
                                        autoComplete="tel-country-code"
                                        defaultValue="+91"
                                        aria-label="Country code"
                                        className={inputClasses}
                                    />
                                    <input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        autoComplete="tel-national"
                                        placeholder="98765 43210"
                                        aria-label="Phone number"
                                        className={inputClasses}
                                    />
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <label
                                    htmlFor="message"
                                    className="text-medium-b text-text-primary"
                                >
                                    Message
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows={5}
                                    required
                                    className={`${inputClasses} resize-y`}
                                />
                            </div>

                            {errorMessage != null && <p className="text-medium text-system-red">{errorMessage}</p>}

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="cta-bg-gradient text-[#FFFFFF] text-nav-text-b px-2 py-1 lg:px-3 lg:py-2 rounded-sm lg:rounded-lg hover:opacity-90 disabled:opacity-60 transition-opacity justify-self-start"
                            >
                                {isSubmitting ? "Sending…" : "Send message"}
                            </button>
                        </Form>
                    )}
                </div>
            </div>
        </section>
    );
}
